import { useNavigate } from 'react-router-dom'
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import { toKebab } from '@/lib/analytics-slugs'

export interface GeographyTopNProps {
  data: Array<{ jurisdiction: string; count: number }>
  totalJurisdictions: number
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

const MIN_ROWS_FOR_THIN = 5
const THIN_PADDING_BOTTOM = 24

/**
 * Horizontal top-10 jurisdictions chart for the SP4 story-first Analytics
 * page (spec §4.2 ⑤). The backend caps `data` at 10 rows; if the hook
 * signals there are more via `totalJurisdictions > data.length`, we append
 * a static "+N more" tail row (v1: non-clickable label; a future `others`
 * bucket from the backend would make it clickable).
 *
 * Row click emits the drill-through URL `/stories?jurisdiction=<kebab>`
 * with the jurisdiction name lower-cased and kebab-slugged. Per spec §4.4,
 * kebab is the URL-visible form — the fetch boundary converts back to the
 * underscored backend enum.
 */
export function GeographyTopN({
  data,
  totalJurisdictions,
  isLoading,
  isError,
  onRetry,
}: GeographyTopNProps) {
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <h3 className="text-sm font-medium mb-3">Top jurisdictions</h3>
        <p className="text-xs text-muted-foreground mb-3">Story coverage by jurisdiction (not incident counts).</p>
        <Skeleton
          data-testid="geography-top-n-skeleton"
          className="h-64 w-full"
        />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <h3 className="text-sm font-medium mb-3">Top jurisdictions</h3>
        <p className="text-xs text-muted-foreground mb-3">Story coverage by jurisdiction (not incident counts).</p>
        <ErrorState
          title="Failed to load jurisdictions."
          onRetry={onRetry}
        />
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <h3 className="text-sm font-medium mb-2">Top jurisdictions</h3>
        <p className="text-xs text-muted-foreground mb-3">Story coverage by jurisdiction (not incident counts).</p>
        <p className="text-sm text-muted-foreground">
          No jurisdictions in the selected range. Try switching the time range
          above.
        </p>
      </div>
    )
  }

  const slugify = (name: string): string =>
    toKebab(name.toLowerCase().replace(/\s+/g, '-'))

  const handleBarClick = (entry: { jurisdiction: string; count: number }) => {
    navigate(`/stories?jurisdiction=${slugify(entry.jurisdiction)}`)
  }

  const thinPad = data.length < MIN_ROWS_FOR_THIN ? THIN_PADDING_BOTTOM : 0
  const moreCount = Math.max(0, totalJurisdictions - data.length)

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="text-sm font-medium mb-3">Top jurisdictions</h3>
      <p className="text-xs text-muted-foreground mb-3">Story coverage by jurisdiction (not incident counts).</p>

      <div
        className="h-64 w-full"
        style={{ paddingBottom: thinPad }}
        data-testid="geography-top-n-chart"
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 0, right: 12, left: 8, bottom: 0 }}
          >
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis
              type="category"
              dataKey="jurisdiction"
              tick={{ fontSize: 11 }}
              width={120}
            />
            <Tooltip cursor={{ fill: 'var(--chart-muted)', opacity: 0.08 }} />
            <Bar
              dataKey="count"
              fill="var(--chart-primary)"
              radius={[0, 2, 2, 0]}
              onClick={(entry) =>
                handleBarClick(entry as { jurisdiction: string; count: number })
              }
              style={{ cursor: 'pointer' }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Clickable row mirrors so keyboard + jsdom (no SVG geometry) still
          get drill-through parity with the chart bars. */}
      <ul className="mt-2 divide-y divide-border/60 text-sm">
        {data.map((row) => {
          const slug = slugify(row.jurisdiction)
          return (
            <li key={row.jurisdiction}>
              <button
                type="button"
                data-testid={`geography-top-n-row-${slug}`}
                onClick={() => handleBarClick(row)}
                className="flex w-full items-center justify-between px-1 py-1.5 text-left hover:bg-muted/40 rounded"
              >
                <span>{row.jurisdiction}</span>
                <span className="tabular-nums text-muted-foreground">
                  {row.count}
                </span>
              </button>
            </li>
          )
        })}
        {moreCount > 0 && (
          <li
            data-testid="geography-top-n-more"
            className="flex items-center justify-between px-1 py-1.5 text-muted-foreground italic"
          >
            <span>+{moreCount} more</span>
          </li>
        )}
      </ul>
    </div>
  )
}
